import * as React from 'react';

import {
  BetType,
  ReleaseStatus,
  SingleRaceReleaseInfo,
} from '@/lib/apollo/types/gen/graphql';
import { BetData, BetMethod, BetTypeList, WheelAxisList } from '@/modelTypes';

import BetTypeCheckBox from './CheckBox';
import BetTypeRadio from './Radio';
import WheelAxisSelector from './WheelAxisSelector';

interface BetTypeSelectorProps {
  betMethod: BetMethod;
  releaseInfo: SingleRaceReleaseInfo;
  betTypeList: BetTypeList;
  modifyBetDetailKey: string;
  isModifyingDisabled: (
    key: 'betMethod' | 'betType' | 'wheelAxis',
    arg: string,
  ) => boolean;
  betData: BetData;
  setBetTypeList: React.Dispatch<React.SetStateAction<BetTypeList>>;
  wheelAxisList: WheelAxisList;
  setWheelAxisList: React.Dispatch<React.SetStateAction<WheelAxisList>>;
}

const BetTypeSelector: React.FC<BetTypeSelectorProps> = React.memo(
  ({
    betTypeList,
    releaseInfo,
    betMethod,
    isModifyingDisabled,
    setBetTypeList,
    wheelAxisList,
    setWheelAxisList,
  }) => {
    const buttonList: { betType: BetType; disabled?: boolean }[] = [
      {
        betType: BetType.Trifecta,
        disabled: releaseInfo.trifectaReleaseStatus !== ReleaseStatus.Released,
      },
      {
        betType: BetType.Trio,
        disabled: releaseInfo.trioReleaseStatus !== ReleaseStatus.Released,
      },
      {
        betType: BetType.Perfecta,
        disabled: releaseInfo.perfectaReleaseStatus !== ReleaseStatus.Released,
      },
      {
        betType: BetType.Quinella,
        disabled: releaseInfo.quinellaReleaseStatus !== ReleaseStatus.Released,
      },
      {
        betType: BetType.BracketPerfecta,
        disabled:
          releaseInfo.bracketPerfectaReleaseStatus !== ReleaseStatus.Released,
      },
      {
        betType: BetType.BracketQuinella,
        disabled:
          releaseInfo.bracketQuinellaReleaseStatus !== ReleaseStatus.Released,
      },
      {
        betType: BetType.QuinellaPlace,
        disabled:
          releaseInfo.quinellaPlaceReleaseStatus !== ReleaseStatus.Released,
      },
    ];

    const handleChange = (checked: boolean, betType: BetType) => {
      setBetTypeList(prev => {
        const newList = { ...prev };
        if (betMethod === BetMethod.Formation || betMethod === BetMethod.Box) {
          if (checked) {
            newList[betMethod].push(betType);
          } else {
            newList[betMethod] = newList[betMethod].filter(
              bt => bt !== betType,
            );
          }
        } else {
          newList[betMethod] = betType;
        }
        return newList;
      });
    };

    return (
      <>
        {(betMethod === BetMethod.Formation || betMethod === BetMethod.Box) && (
          <BetTypeCheckBox
            selectBetTypes={betTypeList[betMethod]}
            buttonList={buttonList}
            isModifyingDisabled={isModifyingDisabled}
            onChange={(checked, betType) => handleChange(checked, betType)}
          />
        )}
        {betMethod === BetMethod.Wheel && (
          <>
            <BetTypeRadio
              betType={betTypeList[betMethod]}
              buttonList={buttonList}
              isModifyingDisabled={isModifyingDisabled}
              onChange={(checked, betType) => handleChange(checked, betType)}
            />
            <WheelAxisSelector
              betType={betTypeList[betMethod]}
              wheelAxis={wheelAxisList[betTypeList[betMethod]]}
              isModifyingDisabled={isModifyingDisabled}
              setWheelAxisList={setWheelAxisList}
            />
          </>
        )}
        {betMethod === BetMethod.Odds && (
          <BetTypeRadio
            betType={betTypeList[betMethod]}
            buttonList={buttonList}
            isModifyingDisabled={isModifyingDisabled}
            onChange={(checked, betType) => handleChange(checked, betType)}
          />
        )}
      </>
    );
  },
);

export default BetTypeSelector;
